import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useRef } from "react";
import { trackEvent } from "../../utils/analytics";
import { calcualteStaffingData } from "../../utils/nursePanelUtils";
import { type AddStaffDate, type ShownDay, useAddStaffDate } from "../AddNurse/useAddStaffDate";
import type { NursesMatrixSelectionMode } from "../NursesMatrixGrid/consts";
import { PanelDayFallback } from "../StaffingChanges/PanelDayFallback";
import { RemoveStaffDialogs } from "./RemoveStaff/RemoveStaffDialogs";
import { RemoveStaffFooter } from "./RemoveStaff/RemoveStaffFooter";
import { RemoveStaffGrid } from "./RemoveStaff/RemoveStaffGrid";
import { RemoveStaffHeader } from "./RemoveStaff/RemoveStaffHeader";
import { RemoveStaffWindowFilter } from "./RemoveStaff/RemoveStaffWindowFilter";
import { REMOVE_STAFF_ANALYTICS_SOURCE, type RemoveStaffWindowHint } from "./RemoveStaff/removeStaffUtils";
import { useRemoveStaffDetails } from "./RemoveStaff/useRemoveStaffDetails";
import { countHiddenPicks, useRemoveStaffFilter } from "./RemoveStaff/useRemoveStaffFilter";
import { useRemoveStaffReadBack } from "./RemoveStaff/useRemoveStaffReadBack";
import { useRemoveStaffRows } from "./RemoveStaff/useRemoveStaffRows";
import { useRemoveStaffSelection } from "./RemoveStaff/useRemoveStaffSelection";
import { useRemoveStaffSubmit } from "./RemoveStaff/useRemoveStaffSubmit";

/** A staffing date as the key its picks and its "open" event are remembered under. */
const DATE_KEY_FORMAT = "yyyy-MM-dd";

type NurseRemovalContentProps = {
	/** The schedule day the panel opens on. */
	date: DateTime;
	showActual: boolean;
	/** The caller, as the panel's long-standing analytics events report it. */
	source: string;
	onClose: (success?: boolean) => void;
	/**
	 * Several shifts at once (checkboxes) or exactly one (radio). A caller acting on one issue — a
	 * recommendation's "remove someone from this shift" — stays single.
	 */
	selectionMode: NursesMatrixSelectionMode;
	/** Let the header's date move the whole tab to another loaded day (worked hours stay on their own day). */
	allowDateChange: boolean;
	/** Told about every date picked in the header, so the caller's other tabs follow it. */
	onSelectedDateChange?: (date: DateTime) => void;
	/** Where the list opens — the caller's shift window; `null` opens on "All shifts". */
	initialWindow: RemoveStaffWindowHint | null;
	/**
	 * The panel is content-sized — its grid grows with its rows up to `STAFF_PICKER.REMOVE_GRID_MAX_HEIGHT`, the
	 * footer right under it — and this is its ceiling, a token set by the host: the Staffing Changes popup's tab
	 * body (`STAFF_PICKER.PANEL_HEIGHT`) or the approval popup's cap (`APPROVAL_DIALOG.MAX_HEIGHT`). Only a very
	 * short window reaches it; the panel then scrolls as a whole, so the footer can still be reached.
	 */
	maxHeight: string;
};

/** The panel's props once its day is on screen: the day and the weeks around it. */
type NurseRemovalDayProps = Omit<NurseRemovalContentProps, "date"> & { panelDay: AddStaffDate; shown: ShownDay };

/** The list side of the panel: the date's rows, the window pills and the picks — for the day on screen. */
const useRemoveStaffList = (props: NurseRemovalDayProps, { weeks, weekIndex, dayNo }: ShownDay) => {
	const model = useRemoveStaffRows({ weeks, weekIndex, dayNo });
	const filter = useRemoveStaffFilter({
		rows: model.rows,
		windows: model.windows,
		otherCount: model.otherCount,
		placementByShiftId: model.placementByShiftId,
		hint: props.initialWindow,
	});
	const selection = useRemoveStaffSelection({
		mode: props.selectionMode,
		rows: model.rows,
		visibleRows: filter.visibleRows,
		dateKey: model.staffingDate.toFormat(DATE_KEY_FORMAT),
	});
	const hiddenPickCounts = useMemo(
		() => countHiddenPicks(selection.selectedRows, filter.activeKey, model.placementByShiftId),
		[selection.selectedRows, filter.activeKey, model.placementByShiftId],
	);
	return { model, filter, selection, hiddenPickCounts };
};

type OpenEvent = { weeks: ShownDay["weeks"]; weekIndex: number; dateKey: string; shiftCount: number; loading: boolean; source: string };

/**
 * "Open Nurse Removal Content", once per date the panel moves to (after its list arrives) — where the removal
 * funnel starts. The list refetching under the open panel (a removal, a refresh) is not a new open.
 */
const useTrackOpen = ({ weeks, weekIndex, dateKey, shiftCount, loading, source }: OpenEvent) => {
	// A SET, not the last key. Remembering only the most recent pair meant stepping back to a date already
	// visited emitted its open a second time, so the funnel counted one panel session as several starts.
	const trackedKeys = useRef<Set<string>>(new Set());
	useEffect(() => {
		const key = `${dateKey}|${source}`;
		if (loading || trackedKeys.current.has(key)) return;
		trackedKeys.current.add(key);
		trackEvent("Open Nurse Removal Content", { staffingData: calcualteStaffingData({ weeks }, weekIndex), shift_count: shiftCount, date: dateKey, source });
	}, [dateKey, source, loading, weeks, weekIndex, shiftCount]);
};

/** The panel's whole model: the date, the list, the removal's details, the confirm flow and the read-back. */
const useRemoveStaffPanel = (props: NurseRemovalDayProps) => {
	const { weeks, weekIndex, dayNo } = props.shown;
	const { model, filter, selection, hiddenPickCounts } = useRemoveStaffList(props, props.shown);
	const details = useRemoveStaffDetails();
	const submit = useRemoveStaffSubmit({
		selectedRows: selection.selectedRows,
		weeks,
		weekIndex,
		dayNo,
		staffingDate: model.staffingDate,
		showActual: props.showActual,
		details,
		source: props.source,
		replaceSelection: selection.replaceSelection,
		onClose: props.onClose,
	});
	const readBack = useRemoveStaffReadBack({ rows: selection.selectedRows, weeks, weekIndex, dayNo, staffingDate: model.staffingDate });
	useTrackOpen({
		weeks,
		weekIndex,
		dateKey: model.staffingDate.toFormat(DATE_KEY_FORMAT),
		shiftCount: model.rows.length,
		loading: model.loading,
		source: props.source,
	});
	return { model, filter, selection, hiddenPickCounts, details, submit, readBack };
};

/**
 * Remove Staff: every shift on the date, grouped by time window with filter pills, one or several picked, one
 * reason for all of them, and a read-back of who comes off before the button does it. Shifts starting inside
 * the call-off window get one "find a replacement?" choice; the rest come off in one publish.
 */
const NurseRemovalContentForDay: FC<NurseRemovalDayProps> = (props) => {
	const { model, filter, selection, hiddenPickCounts, details, submit, readBack } = useRemoveStaffPanel(props);
	return (
		<Box sx={{ display: "flex", flexDirection: "column", maxHeight: props.maxHeight, overflowY: "auto" }} data-testid="remove-staff-panel">
			<RemoveStaffHeader staffingDate={model.staffingDate} datePicker={props.panelDay} />
			<Box sx={{ flex: "none", display: "flex", flexDirection: "column", px: 3, pb: 2 }}>
				<RemoveStaffGrid
					// A fresh grid per date: folded window bands and the search belong to the date they were set on,
					// so the same window on the next date doesn't open collapsed.
					key={model.staffingDate.toFormat(DATE_KEY_FORMAT)}
					rows={filter.visibleRows}
					windows={model.windows}
					placementByShiftId={model.placementByShiftId}
					activeFilter={filter.activeKey}
					dateRange={model.dateRange}
					dayNo={props.shown.dayNo}
					staffingDate={model.staffingDate}
					selectionMode={props.selectionMode}
					selectedRowIds={selection.gridSelectedIds}
					onSelectionChange={selection.onGridSelectionChange}
					loading={model.loading}
					filters={
						<RemoveStaffWindowFilter
							windows={model.windows}
							otherCount={model.otherCount}
							totalCount={model.rows.length}
							activeKey={filter.activeKey}
							hiddenPickCounts={hiddenPickCounts}
							onChange={filter.changeFilter}
						/>
					}
				/>
			</Box>
			<RemoveStaffFooter
				details={details}
				summary={readBack.summary}
				selectedStaff={readBack.selectedStaff}
				unionWarning={readBack.unionWarning}
				confirmLabel={readBack.confirmLabel}
				canSubmit={selection.selectedRows.length > 0}
				confirmLoading={submit.isSubmitting}
				onCancel={() => props.onClose(false)}
				onConfirm={() => void submit.requestRemove()}
			/>
			<RemoveStaffDialogs choiceDialog={submit.choiceDialog} payrollDialog={submit.payrollDialog} />
		</Box>
	);
};

export const NurseRemovalContent: FC<NurseRemovalContentProps> = ({ date, ...rest }) => {
	const panelDay = useAddStaffDate({
		initialDate: date,
		enabled: rest.allowDateChange && !rest.showActual,
		onSelectedDateChange: rest.onSelectedDateChange,
		analyticsSource: REMOVE_STAFF_ANALYTICS_SOURCE,
	});
	if (!panelDay.shown) return <PanelDayFallback panelDay={panelDay} dataTestId="remove-staff-panel" />;
	return <NurseRemovalContentForDay {...rest} panelDay={panelDay} shown={panelDay.shown} />;
};
